import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService, { onLogoutEvent } from '../services/api';

interface User {
  id: string;
  email: string;
  subscription_status: string;
  subscription_tier: string;
  is_admin?: boolean;
  wallet_address?: string;
  wallet_security?: {
    has_exported_key: boolean;
    should_show_reminder: boolean;
  };
  has_completed_onboarding?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, passwordConfirm: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  // Pending plan from website URL parameter
  pendingPlanId: number | null;
  setPendingPlanId: (id: number | null) => void;
  clearPendingPlanId: () => void;
  // Onboarding
  markOnboardingComplete: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Read pending plan ID from localStorage (captured by index.html script)
const getPendingPlanFromStorage = (): number | null => {
  const storedPlanId = localStorage.getItem('pendingPlanId');
  if (storedPlanId) {
    const planId = parseInt(storedPlanId, 10);
    if (!isNaN(planId)) {
      return planId;
    }
  }
  return null;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Read pending plan from localStorage (set by index.html inline script)
  const [pendingPlanId, setPendingPlanIdState] = useState<number | null>(getPendingPlanFromStorage);

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      apiService.setAuthToken(storedToken);
    }

    setIsLoading(false);
  }, []);

  const setPendingPlanId = (id: number | null) => {
    setPendingPlanIdState(id);
    if (id !== null) {
      localStorage.setItem('pendingPlanId', String(id));
    } else {
      localStorage.removeItem('pendingPlanId');
    }
  };

  const clearPendingPlanId = () => {
    setPendingPlanIdState(null);
    localStorage.removeItem('pendingPlanId');
  };

  const markOnboardingComplete = useCallback(() => {
    if (user) {
      const updatedUser = { ...user, has_completed_onboarding: true };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }, [user]);

  const login = async (email: string, password: string) => {
    const response = await apiService.login(email, password);
    setToken(response.token);
    setUser(response.user);
    localStorage.setItem('token', response.token);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('user', JSON.stringify(response.user));
    apiService.setAuthToken(response.token);
  };

  const register = async (email: string, password: string, passwordConfirm: string) => {
    const response = await apiService.register(email, password, passwordConfirm);
    setToken(response.token);
    setUser(response.user);
    localStorage.setItem('token', response.token);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('user', JSON.stringify(response.user));
    apiService.setAuthToken(response.token);
  };

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    apiService.setAuthToken(null);
  }, []);

  // Listen for logout events triggered by the API interceptor (e.g., when token refresh fails)
  useEffect(() => {
    const unsubscribe = onLogoutEvent(() => {
      logout();
    });
    return unsubscribe;
  }, [logout]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      register,
      logout,
      isLoading,
      pendingPlanId,
      setPendingPlanId,
      clearPendingPlanId,
      markOnboardingComplete,
    }}>
      {children}
    </AuthContext.Provider>
  );
};